if (typeof GiftProgressBar !== 'function') {

  class GiftProgressBar extends HTMLElement {

    constructor() {
      super();
      this.carousels = [];
      this._boundClickHandler = null;
      this._boundChangeHandler = null;
      this._boundKeydownHandler = null;
    }

    // Use getters to always read fresh values from DOM
    get tier1Threshold() {
      return parseInt(this.getAttribute('data-tier1-threshold'), 10) || 0;
    }

    get tier2Threshold() {
      return parseInt(this.getAttribute('data-tier2-threshold'), 10) || 0;
    }

    get tier3Threshold() {
      return parseInt(this.getAttribute('data-tier3-threshold'), 10) || 0;
    }

    get maxThreshold() {
      return this.tier3Threshold;
    }

    connectedCallback() {
      // Called when element is added to DOM - ensures attributes are available
      this.init();
    }

    disconnectedCallback() {
      // Cleanup carousels when element is removed
      this.carousels.forEach(embla => embla.destroy());
      this.carousels = [];
    }

    async init() {
      // Bind events first (synchronously) to ensure they work immediately
      this.bindEvents();

      // Then load Embla and init carousels (async, can fail without breaking events)
      try {
        await this.loadEmbla();
        this.initCarousels();
      } catch (error) {
        console.warn('Embla carousel failed to load:', error);
      }

      this.setupCartObserver();
    }

    async loadEmbla() {
      if (window.EmblaCarousel) return;

      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/embla-carousel@8.3.0/embla-carousel.umd.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    initCarousels() {
      const carouselContainers = this.querySelectorAll('[data-gift-carousel]');

      carouselContainers.forEach(container => {
        const viewport = container.querySelector('[data-carousel-viewport]');
        const prevBtn = container.querySelector('[data-carousel-prev]');
        const nextBtn = container.querySelector('[data-carousel-next]');
        const dotsContainer = container.parentElement.querySelector('[data-carousel-dots]');

        if (!viewport || !window.EmblaCarousel) return;

        const embla = window.EmblaCarousel(viewport, {
          align: 'start',
          containScroll: 'trimSnaps',
          dragFree: false,
          slidesToScroll: 1
        });

        this.carousels.push(embla);

        // Handle prev/next buttons
        if (prevBtn && nextBtn) {
          const updateButtons = () => {
            prevBtn.disabled = !embla.canScrollPrev();
            nextBtn.disabled = !embla.canScrollNext();
          };

          prevBtn.addEventListener('click', () => embla.scrollPrev());
          nextBtn.addEventListener('click', () => embla.scrollNext());

          embla.on('select', updateButtons);
          embla.on('init', updateButtons);
          embla.on('reInit', updateButtons);

          updateButtons();
        }

        // Handle pagination dots
        if (dotsContainer) {
          const dots = dotsContainer.querySelectorAll('[data-carousel-dot]');

          const updateDots = () => {
            const selectedIndex = embla.selectedScrollSnap();
            dots.forEach((dot, index) => {
              dot.classList.toggle('gift-carousel__dot--active', index === selectedIndex);
            });
          };

          dots.forEach(dot => {
            dot.addEventListener('click', () => {
              const index = parseInt(dot.getAttribute('data-carousel-dot'), 10);
              embla.scrollTo(index);
            });
          });

          embla.on('select', updateDots);
          embla.on('init', updateDots);
          embla.on('reInit', updateDots);

          updateDots();
        }
      });
    }

    bindEvents() {
      // Prevent duplicate event binding
      if (this._eventsbound) return;
      this._eventsbound = true;

      // Use event delegation for dynamically rendered content
      // This ensures new buttons added after cart updates still work
      this.addEventListener('click', (e) => {
        // Handle add gift button clicks
        const addButton = e.target.closest('[data-add-gift]');
        if (addButton) {
          e.stopPropagation();
          e.preventDefault();
          this.handleAddGift(addButton);
          return;
        }

        // Handle gift preview card clicks
        const previewCard = e.target.closest('[data-gift-preview]');
        if (previewCard && !e.target.closest('[data-add-gift]') && !e.target.closest('[data-gift-variant-select]')) {
          this.handlePreviewClick(previewCard);
          return;
        }

        // Handle modal close button clicks
        const closeButton = e.target.closest('[data-gift-modal-close]');
        if (closeButton) {
          this.closeModal();
          return;
        }
      });

      // Handle variant selector changes via delegation
      this.addEventListener('change', (e) => {
        const select = e.target.closest('[data-gift-variant-select]');
        if (select) {
          e.stopPropagation();
          const selectedVariantId = select.value;
          const card = select.closest('.gift-product');
          if (card) {
            const addBtn = card.querySelector('[data-add-gift]');
            if (addBtn) {
              addBtn.setAttribute('data-variant-id', selectedVariantId);
              // Check if selected variant is available
              const selectedOption = select.options[select.selectedIndex];
              addBtn.disabled = selectedOption.disabled;
            }
            // Update card's data-variant-id for modal
            card.setAttribute('data-variant-id', selectedVariantId);
          }
        }
      });

      // Keyboard support for accessibility on preview cards
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          const previewCard = e.target.closest('[data-gift-preview]');
          if (previewCard && !e.target.closest('[data-add-gift]') && !e.target.closest('[data-gift-variant-select]')) {
            e.preventDefault();
            this.handlePreviewClick(previewCard);
          }
        }
      });

      // Close modal on escape key
      document.addEventListener('keydown', (e) => {
        const modal = this.querySelector('[data-gift-modal]');
        if (e.key === 'Escape' && modal && !modal.hidden) {
          this.closeModal();
        }
      });
    }

    handlePreviewClick(productCard) {
      const modal = this.querySelector('[data-gift-modal]');
      if (!productCard || !modal) return;

      const title = productCard.dataset.productTitle;
      const image = productCard.dataset.productImage;
      const url = productCard.dataset.productUrl;
      const tier = productCard.dataset.tier;
      const isLocked = productCard.classList.contains('gift-product--locked');

      // Get threshold for this tier
      let threshold = 0;
      switch (parseInt(tier, 10)) {
        case 1:
          threshold = this.tier1Threshold;
          break;
        case 2:
          threshold = this.tier2Threshold;
          break;
        case 3:
          threshold = this.tier3Threshold;
          break;
      }

      const modalImage = modal.querySelector('[data-gift-modal-image]');
      const modalTitle = modal.querySelector('[data-gift-modal-title]');
      const modalDesc = modal.querySelector('[data-gift-modal-description]');
      const modalLink = modal.querySelector('[data-gift-modal-link]');

      if (modalImage) {
        modalImage.src = image;
        modalImage.alt = title;
      }
      if (modalTitle) {
        modalTitle.textContent = isLocked
          ? (window.KROWN?.settings?.locales?.gift_modal_locked_title || 'Gratis') + ' ' + title
          : (window.KROWN?.settings?.locales?.gift_modal_unlocked_title || 'Gratis') + ' ' + title;
      }
      if (modalDesc) {
        const thresholdFormatted = this._formatMoney(threshold, window.KROWN?.settings?.shop_money_format || '{{amount_with_comma_separator}} €');
        modalDesc.textContent = isLocked
          ? (window.KROWN?.settings?.locales?.gift_modal_locked_desc || 'Schalte dieses gratis Produkt frei ab einem Einkaufswert von {{ threshold }}!').replace('{{ threshold }}', thresholdFormatted)
          : (window.KROWN?.settings?.locales?.gift_modal_unlocked_desc || 'Dieses Geschenk ist freigeschaltet! Wähle es jetzt aus.');
      }
      if (modalLink) {
        modalLink.href = url;
      }

      modal.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    closeModal() {
      const modal = this.querySelector('[data-gift-modal]');
      if (modal) {
        modal.hidden = true;
        document.body.style.overflow = '';
      }
    }

    async handleAddGift(button) {
      const variantId = button.getAttribute('data-variant-id');
      const tier = button.getAttribute('data-tier');

      if (!variantId || button.disabled) return;

      // Check if customer is allowed to claim this gift
      const canClaim = await this.canClaimGift(parseInt(tier, 10));
      if (!canClaim) return;

      button.disabled = true;
      button.classList.add('gift-product__add-btn--loading');

      // Map tier to Kite rule ID
      const ruleIdMap = {
        1: 'SPEND_X_0',
        2: 'SPEND_X_1',
        3: 'SPEND_X_2'
      };
      const ruleId = ruleIdMap[parseInt(tier, 10)] || 'SPEND_X_0';

      try {
        const response = await fetch(window.KROWN.settings.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId, 10),
              quantity: 1,
              properties: {
                // Kite app required properties
                '_free_product': 'true',
                '_rule_id': ruleId,
                '_kite_promo_name': 'Geschenk Campaign (50,80,140 EUR)',
                // Keep our own properties for UI management
                '_is_gift': 'true',
                '_gift_tier': tier
              }
            }]
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add gift');
        }

        // Refresh the cart drawer using the theme's Cart controller
        if (window.CartDrawer && typeof window.CartDrawer.Cart.refresh === 'function') {
          window.CartDrawer.Cart.refresh();
        } else {
          // Fallback: fetch helper-cart section and update drawer
          fetch('?section_id=helper-cart')
            .then(res => res.text())
            .then(html => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const newDrawer = doc.querySelector('[data-cart-drawer]');
              const drawer = document.querySelector('[data-cart-drawer]');
              if (newDrawer && drawer) {
                drawer.innerHTML = newDrawer.innerHTML;
              }
              const newFooter = doc.querySelector('[data-cart-footer]');
              const oldFooter = document.querySelector('[data-cart-footer]');
              if (newFooter && oldFooter) {
                oldFooter.outerHTML = newFooter.outerHTML;
              }
              // Update cart count in header
              const cartCount = doc.querySelector('[data-cart-count]')?.textContent || '0';
              document.querySelectorAll('[data-header-cart-count]').forEach(el => {
                el.textContent = cartCount;
              });
              // Update sidebar empty state
              const sidebar = document.getElementById('site-cart-sidebar');
              if (sidebar) {
                sidebar.classList.toggle('cart-is-empty', cartCount === '0');
              }
            });
        }

      } catch (error) {
        console.error('Error adding gift:', error);
        button.disabled = false;
        button.classList.remove('gift-product__add-btn--loading');
      }
    }

    async canClaimGift(tierNum) {
      try {
        const response = await fetch(window.KROWN.settings.routes.cart_url + '.js');
        const cart = await response.json();

        // Calculate cart total excluding gift items (items with _free_product: "true")
        let cartTotalForGifts = cart.total_price;
        const claimedTiers = [];

        cart.items.forEach(item => {
          // Check for Kite's _free_product property (primary) or legacy _is_gift
          const isFreeProduct = item.properties && (
            item.properties['_free_product'] === 'true' ||
            item.properties['_is_gift'] === 'true'
          );
          if (isFreeProduct) {
            cartTotalForGifts -= item.final_line_price;
            const giftTier = item.properties['_gift_tier'];
            if (giftTier) {
              claimedTiers.push(parseInt(giftTier, 10));
            }
          }
        });

        // Check if tier threshold is met
        let threshold = 0;
        switch (tierNum) {
          case 1:
            threshold = this.tier1Threshold;
            break;
          case 2:
            threshold = this.tier2Threshold;
            break;
          case 3:
            threshold = this.tier3Threshold;
            break;
        }

        // Check if cart meets threshold and tier not already claimed
        const meetsThreshold = cartTotalForGifts >= threshold;
        const notClaimed = !claimedTiers.includes(tierNum);

        return meetsThreshold && notClaimed;
      } catch (error) {
        console.error('Error checking gift eligibility:', error);
        return false;
      }
    }

    setupCartObserver() {
      // Listen for cart updates from Shopify's AJAX cart
      document.addEventListener('cart:refresh', () => this.refreshCart());

      // Listen for the theme's cart-updated event
      const cartForm = document.getElementById('AjaxCartForm');
      if (cartForm) {
        cartForm.addEventListener('cart-updated', () => this.refreshCart());
      }
    }

    async refreshCart() {
      try {
        const response = await fetch(window.KROWN.settings.routes.cart_url + '.js');
        const cart = await response.json();
        await this.updateFromCart(cart);
      } catch (error) {
        console.error('Error refreshing cart:', error);
      }
    }

    async updateFromCart(cart) {
      // Calculate cart total excluding gift items
      // Use cart.total_price (after all discounts) minus gift items value
      // Check for Kite's _free_product property (primary) or legacy _is_gift
      let giftItemsTotal = 0;
      const giftItems = [];

      cart.items.forEach(item => {
        const isFreeProduct = item.properties && (
          item.properties['_free_product'] === 'true' ||
          item.properties['_is_gift'] === 'true'
        );
        if (isFreeProduct) {
          giftItems.push(item);
          giftItemsTotal += item.final_line_price;
        }
      });

      // Actual amount customer pays for non-gift items (after cart-level discounts)
      const cartTotalForGifts = cart.total_price - giftItemsTotal;

      // Check if any gifts should be removed
      await this.removeInvalidGifts(giftItems, cartTotalForGifts);

      // Update the progress bar UI
      this.updateProgressBar(cartTotalForGifts);
      this.updateMilestones(cartTotalForGifts, giftItems);
      this.updateHeaderText(cartTotalForGifts, giftItems);
    }

    async removeInvalidGifts(giftItems, cartTotal) {
      const itemsToRemove = [];

      giftItems.forEach(item => {
        const giftTier = parseInt(item.properties['_gift_tier'], 10);
        let tierThreshold = 0;

        switch (giftTier) {
          case 1:
            tierThreshold = this.tier1Threshold;
            break;
          case 2:
            tierThreshold = this.tier2Threshold;
            break;
          case 3:
            tierThreshold = this.tier3Threshold;
            break;
        }

        if (cartTotal < tierThreshold) {
          itemsToRemove.push(item);
        }
      });

      // Remove invalid gifts
      for (const item of itemsToRemove) {
        try {
          await fetch(window.KROWN.settings.routes.cart_change_url + '.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: item.key,
              quantity: 0
            })
          });
        } catch (error) {
          console.error('Error removing gift:', error);
        }
      }

      if (itemsToRemove.length > 0) {
        // Trigger cart refresh after removing items
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }

    updateProgressBar(cartTotal) {
      const progressFill = this.querySelector('[data-js-gift-progress-fill]');
      if (!progressFill) return;

      let progressPercent = (cartTotal / this.maxThreshold) * 100;
      if (progressPercent > 100) progressPercent = 100;

      progressFill.style.width = `${progressPercent}%`;
    }

    updateMilestones(cartTotal, giftItems) {
      const claimedTiers = giftItems.map(item => item.properties['_gift_tier']);
      const milestones = this.querySelectorAll('.gift-progress__milestone');

      milestones.forEach(milestone => {
        const tier = milestone.getAttribute('data-tier');
        const tierNum = parseInt(tier, 10);
        let threshold = 0;

        switch (tierNum) {
          case 1:
            threshold = this.tier1Threshold;
            break;
          case 2:
            threshold = this.tier2Threshold;
            break;
          case 3:
            threshold = this.tier3Threshold;
            break;
        }

        const isUnlocked = cartTotal >= threshold;
        const isClaimed = claimedTiers.includes(tier);

        milestone.classList.toggle('gift-progress__milestone--unlocked', isUnlocked);
        milestone.classList.toggle('gift-progress__milestone--claimed', isClaimed);

        // Update icon
        const iconContainer = milestone.querySelector('.gift-progress__milestone-icon');
        if (iconContainer) {
          if (isClaimed) {
            iconContainer.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
          } else {
            iconContainer.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';
          }
        }
      });
    }

    updateHeaderText(cartTotal, giftItems) {
      const headerTitle = this.querySelector('.gift-progress__title');
      if (!headerTitle) return;

      const claimedTiers = giftItems.map(item => item.properties['_gift_tier']);
      const tier1Claimed = claimedTiers.includes('1');
      const tier2Claimed = claimedTiers.includes('2');
      const tier3Claimed = claimedTiers.includes('3');

      // Find next tier to claim
      let showTier = 0;
      let threshold = 0;
      let isUnlocked = false;

      if (!tier1Claimed) {
        showTier = 1;
        threshold = this.tier1Threshold;
        isUnlocked = cartTotal >= threshold;
      } else if (!tier2Claimed) {
        showTier = 2;
        threshold = this.tier2Threshold;
        isUnlocked = cartTotal >= threshold;
      } else if (!tier3Claimed) {
        showTier = 3;
        threshold = this.tier3Threshold;
        isUnlocked = cartTotal >= threshold;
      }

      if (tier3Claimed) {
        headerTitle.textContent = window.KROWN.settings.locales.gift_all_unlocked || 'Alle Geschenke freigeschaltet!';
      } else if (isUnlocked) {
        const text = (window.KROWN.settings.locales.gift_tier_unlocked || 'Wähle dein Geschenk aus Stufe {{ tier }}!')
          .replace('{{ tier }}', showTier);
        headerTitle.textContent = text;
      } else {
        const remaining = threshold - cartTotal;
        const formattedRemaining = this._formatMoney(remaining, window.KROWN.settings.shop_money_format);
        const text = (window.KROWN.settings.locales.gift_remaining || 'Du bist {{ remaining }} von der nächsten Stufe entfernt:')
          .replace('{{ remaining }}', formattedRemaining);
        headerTitle.textContent = text;
      }
    }

    _formatMoney(cents, format) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '{{amount}}';

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number === null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          '$1' + thousands
        );
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
      }

      const match = formatString.match(placeholderRegex);
      if (!match) return cents;

      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return formatString.replace(placeholderRegex, value);
    }

  }

  if (typeof customElements.get('gift-progress-bar') === 'undefined') {
    customElements.define('gift-progress-bar', GiftProgressBar);
  }

}

// FALLBACK: Global click handler for dynamically inserted gift buttons
// This works even when custom element isn't upgraded after AJAX updates
(function() {
  if (window._giftProgressGlobalHandler) return;
  window._giftProgressGlobalHandler = true;

  document.addEventListener('click', async function(e) {
    const addButton = e.target.closest('[data-add-gift]');
    if (!addButton) return;

    // Check if the custom element has already handled this
    const giftProgressBar = addButton.closest('gift-progress-bar');
    if (giftProgressBar && giftProgressBar._eventsbound) return;

    e.stopPropagation();
    e.preventDefault();

    const variantId = addButton.getAttribute('data-variant-id');
    const tier = addButton.getAttribute('data-tier');

    if (!variantId || addButton.disabled) return;

    // Get thresholds from the gift-progress-bar element
    let tier1Threshold = 5000, tier2Threshold = 8000, tier3Threshold = 14000;
    if (giftProgressBar) {
      tier1Threshold = parseInt(giftProgressBar.getAttribute('data-tier1-threshold'), 10) || 5000;
      tier2Threshold = parseInt(giftProgressBar.getAttribute('data-tier2-threshold'), 10) || 8000;
      tier3Threshold = parseInt(giftProgressBar.getAttribute('data-tier3-threshold'), 10) || 14000;
    }

    // Check eligibility
    try {
      const cartResponse = await fetch(window.KROWN.settings.routes.cart_url + '.js');
      const cart = await cartResponse.json();

      let cartTotalForGifts = cart.total_price;
      const claimedTiers = [];

      cart.items.forEach(item => {
        const isFreeProduct = item.properties && (
          item.properties['_free_product'] === 'true' ||
          item.properties['_is_gift'] === 'true'
        );
        if (isFreeProduct) {
          cartTotalForGifts -= item.final_line_price;
          const giftTier = item.properties['_gift_tier'];
          if (giftTier) {
            claimedTiers.push(parseInt(giftTier, 10));
          }
        }
      });

      const tierNum = parseInt(tier, 10);
      let threshold = 0;
      switch (tierNum) {
        case 1: threshold = tier1Threshold; break;
        case 2: threshold = tier2Threshold; break;
        case 3: threshold = tier3Threshold; break;
      }

      const meetsThreshold = cartTotalForGifts >= threshold;
      const notClaimed = !claimedTiers.includes(tierNum);

      if (!meetsThreshold || !notClaimed) return;

      // Add to cart
      addButton.disabled = true;
      addButton.classList.add('gift-product__add-btn--loading');

      const ruleIdMap = { 1: 'SPEND_X_0', 2: 'SPEND_X_1', 3: 'SPEND_X_2' };
      const ruleId = ruleIdMap[tierNum] || 'SPEND_X_0';

      const response = await fetch(window.KROWN.settings.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: {
              '_free_product': 'true',
              '_rule_id': ruleId,
              '_kite_promo_name': 'Geschenk Campaign (50,80,140 EUR)',
              '_is_gift': 'true',
              '_gift_tier': tier
            }
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add gift');
      }

      // Refresh cart drawer
      if (window.CartDrawer && typeof window.CartDrawer.Cart.refresh === 'function') {
        window.CartDrawer.Cart.refresh();
      } else {
        fetch('?section_id=helper-cart')
          .then(res => res.text())
          .then(html => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newDrawer = doc.querySelector('[data-cart-drawer]');
            const drawer = document.querySelector('[data-cart-drawer]');
            if (newDrawer && drawer) {
              drawer.innerHTML = newDrawer.innerHTML;
            }
            document.querySelectorAll('[data-header-cart-count]').forEach(el => {
              const count = doc.querySelector('[data-cart-count]')?.textContent || '0';
              el.textContent = count;
            });
          });
      }

    } catch (error) {
      addButton.disabled = false;
      addButton.classList.remove('gift-product__add-btn--loading');
    }
  });
})();
